import { createAmericanAirlinesLoginUrl } from "Services/urls";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { FLIGHT_SELECT_URL, ROLE_CODES, COMMON_DAYJS_FORMAT, USA_DOLLAR_CODE } from "Services/constants";
import { html } from "lit-html";
import { iconButton } from "Components/iconButton";
import { logOutInline, getRequestUrl } from "Services/common";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";
import { SearchClickedEventData } from "Services/logging/logger/types-and-interfaces/SearchClickedEvent";
import { TestIdDictionary } from "Services/test-ids/TestIdDictionary";
import { useEffectWithoutCleanup, useMemo } from "Shared/haunted/CustomHooks";
import { useLogger } from "Services/logging/logger/useLogger";
import { useNewFlightDateSelector } from "./date-selector/useNewFlightDateSelector";
import { usePromoCodeBox } from "./promo-code-box/usePromoCodeBox";
import { usePassengerSelector } from "./passenger-selector/usePassengerSelector";
import { usePreviousSearch } from "./usePreviousSearch";
import { useRadioToggle } from "../../../useRadioToggle";
import { useReduxState } from "Shared/redux/useReduxState";
import { useStationSelector } from "./station-selector/useStationSelector";
import { useWindowSize } from "Shared/haunted/useWindowSize";
import classNames from "classnames";
import i18next from "i18next";
import SearchRequest from "Shared/models/SearchRequest";
import url from "url";

export interface Props {
	currentSearchManager: CurrentSearchManager;
	culture: string;
	dynamicSettings: DynamicSettings;
	loaderElemId: string;
}

const REDEMPTION_MILES_ID = "redemptionMiles";

export const useFlightsTab = (props: Props) => {
	const windowSize = useWindowSize();

	const { logEvent } = useLogger();

	const [userInfo] = useReduxState("userInfo");

	const isChileCompraUser = () => userInfo?.ChileCompra?.IsMember;
	const isCug2User = () => Boolean(userInfo?.OrganizationName);
	const isPeruCompraUser = () => userInfo?.PeruCompra?.IsMember || userInfo?.PeruCompra?.IsAdmin;

	const showRedemptionMiles = () =>
		window.JetSmart?.DynamicSettings?.IsAmericanOn && !isCug2User() && !isChileCompraUser() && !isPeruCompraUser();

	const previousSearch = usePreviousSearch();
	const [navitaireSettings] = useReduxState("navitaireSettings");

	const promoCodeBox = usePromoCodeBox({
		culture: props.culture,
		currentSearchManager: props.currentSearchManager,
		dynamicSettings: props.dynamicSettings,
	});

	const showReloadLink = useMemo(
		() =>
			Boolean(previousSearch.load()) &&
			(!props.currentSearchManager.currentSearch.useAmericanMiles || !userInfo?.AmericanAirlines?.IsMember),
		[props.currentSearchManager.currentSearch.useAmericanMiles, userInfo?.AmericanAirlines?.IsMember]
	);

	const radioToggle = useRadioToggle({
		isOn: !props.currentSearchManager.currentSearch.isOneWay,
		firstOptionText: i18next.t("Solo ida"),
		secondOptionText: i18next.t("Ida y vuelta"),
		onChange: (isOneWay: boolean) => {
			props.currentSearchManager.setIsOneWay(isOneWay);
			props.currentSearchManager.setReturnDate(
				props.currentSearchManager.currentSearch.isOneWay !== isOneWay
					? undefined
					: props.currentSearchManager.currentSearch.returnDate
			);
		},
	});

	const startLoading = () => {
		const loaderInstance = window.newRtLoader(`#${props.loaderElemId}`);
		loaderInstance.startLoader();
		return loaderInstance;
	};

	const stopLoading = (loader: LoaderInstance) => {
		if (!loader) return;
		loader.stopLoader();
		loader.destroy();
	};

	const stationSelector = useStationSelector({
		currentSearchManager: props.currentSearchManager,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		startLoading,
		stopLoading,
	});

	const passengerSelector = usePassengerSelector({
		culture: props.culture,
		currentSearchManager: props.currentSearchManager,
	});

	const dateSelector = useNewFlightDateSelector({
		currentSearchManager: props.currentSearchManager,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		openPassengerSelector: passengerSelector.open,
		startLoading,
		stopLoading,
	});

	const getSearchRequest = () => {
		const cultureElements = props.culture.split("-", 2);
		const countryIndex = 1;
		const rc = cultureElements.length === 2 ? cultureElements[countryIndex] : "";
		const isRedemptionSearch =
			showRedemptionMiles() &&
			props.currentSearchManager.currentSearch.useAmericanMiles &&
			userInfo?.AmericanAirlines?.IsMember;

		const [promoCode1, promoCode2] = props.currentSearchManager.currentSearch.promoCodes;

		let req: SearchRequest = {
			o1: props.currentSearchManager.currentSearch.originCity.code,
			d1: props.currentSearchManager.currentSearch.destinationCity.code,
			dd1: props.currentSearchManager.currentSearch.departureDate.format(COMMON_DAYJS_FORMAT),
			ADT:
				props.currentSearchManager.currentSearch.passengers.adults +
				props.currentSearchManager.currentSearch.passengers.teens,
			CHD: props.currentSearchManager.currentSearch.passengers.children,
			inl: props.currentSearchManager.currentSearch.passengers.infants,
			r: false,
			s: true,
			mon: true,
			cur: !isRedemptionSearch ? navitaireSettings.currency : USA_DOLLAR_CODE,
			culture: props.culture,
			pc: !isRedemptionSearch ? promoCode1 || "" : "",
			rc,
			selectedPriceOutbound: dateSelector.selectedPriceOutbound?.toString().trim() || "",
			selectedPriceInbound: "",
			isRedemptionFlow: isRedemptionSearch || false,
		};

		if (!props.currentSearchManager.currentSearch.isOneWay) {
			req = {
				...req,
				dd2: props.currentSearchManager.currentSearch.returnDate.format(COMMON_DAYJS_FORMAT),
				selectedPriceInbound: dateSelector.selectedPriceInbound?.toString().trim() || "",
				r: true,
			};
		}

		if (promoCode2) {
			req = {
				...req,
				pc2: !isRedemptionSearch ? promoCode2 : "",
			};
		}

		return req;
	};

	const redirectToQueueItUrl = (requestUrl: string) => {
		const newLocationUrl = encodeURIComponent(
			url.resolve(window.JetSmart.Settings.BookingUrl, `${FLIGHT_SELECT_URL}${requestUrl}`)
		);

		window.location.href = props.dynamicSettings.QueueItPrefix + newLocationUrl;
	};

	const redirectToRegularUrl = (requestUrl: string) => {
		location.href = url.resolve(window.JetSmart.Settings.BookingUrl, `${FLIGHT_SELECT_URL}${requestUrl}`);
	};

	const redirectToAmericanAirlinesLogin = async () => {
		if (userInfo?.RoleCode !== ROLE_CODES.WEB_ANONYMOUS) {
			await logOutInline();
		}

		window.location.href = createAmericanAirlinesLoginUrl(props.culture);
		return;
	};

	const savePreviousSearch = () => {
		previousSearch.save({
			departureDate: props.currentSearchManager.currentSearch.departureDate.format(COMMON_DAYJS_FORMAT),
			destination: props.currentSearchManager.currentSearch.destinationCity.code,
			isOneWay: props.currentSearchManager.currentSearch.isOneWay,
			origin: props.currentSearchManager.currentSearch.originCity.code,
			passengers: {
				adults: props.currentSearchManager.currentSearch.passengers.adults,
				children: props.currentSearchManager.currentSearch.passengers.children,
				infants: props.currentSearchManager.currentSearch.passengers.infants,
				teens: props.currentSearchManager.currentSearch.passengers.teens,
			},
			returnDate: props.currentSearchManager.currentSearch.returnDate?.format(COMMON_DAYJS_FORMAT) || "",
		});
	};

	const handleReloadSearch = async (e: MouseEvent) => {
		e.preventDefault();

		const value = previousSearch.load();
		if (!value) return;

		try {
			props.currentSearchManager.reloadSearch(value, props.currentSearchManager.currentSearch.promoCodes);
		} catch {
			// Do nothing
		}
	};

	const handleFormSubmit = async () => {
		if (
			!props.currentSearchManager.currentSearch.originCity ||
			!props.currentSearchManager.currentSearch.destinationCity ||
			!props.currentSearchManager.currentSearch.departureDate ||
			(!props.currentSearchManager.currentSearch.isOneWay &&
				!props.currentSearchManager.currentSearch.returnDate) ||
			props.currentSearchManager.currentSearch.passengers.adults +
				props.currentSearchManager.currentSearch.passengers.teens ===
				0
		) {
			return;
		}

		logEvent<SearchClickedEventData>("search_clicked", {
			adults: props.currentSearchManager.currentSearch.passengers.adults,
			children: props.currentSearchManager.currentSearch.passengers.children,
			culture: props.culture,
			departureDate: props.currentSearchManager.currentSearch.departureDate,
			destinationCode: props.currentSearchManager.currentSearch.destinationCity.code,
			infants: props.currentSearchManager.currentSearch.passengers.infants,
			numberOfJourneys: props.currentSearchManager.currentSearch.isOneWay ? 1 : 2,
			market:
				props.currentSearchManager.currentSearch.destinationCity.code !==
				props.currentSearchManager.currentSearch.originCity.code
					? "INTER"
					: `DOM-${props.currentSearchManager.currentSearch.originCity.code}`,
			originCode: props.currentSearchManager.currentSearch.originCity.code,
			preloadedPromoCodes: promoCodeBox.predefinedPromoCodes,
			promoCodes: props.currentSearchManager.currentSearch.promoCodes,
			returnDate: props.currentSearchManager.currentSearch.returnDate,
			teens: props.currentSearchManager.currentSearch.passengers.teens,
		});

		if (
			props.currentSearchManager.currentSearch.promoCodes.length > 0 &&
			props.currentSearchManager.currentSearch.useAmericanMiles &&
			!userInfo?.AmericanAirlines?.IsMember
		) {
			PUB_SUBS.RedemptionNoPromoCodeModalOpened.publish({});
			return;
		}

		if (props.currentSearchManager.currentSearch.useAmericanMiles && !userInfo?.AmericanAirlines?.IsMember) {
			await redirectToAmericanAirlinesLogin();
			return;
		}

		startLoading();

		savePreviousSearch();

		const request = getSearchRequest();
		const requestUrl = getRequestUrl(request);

		if (props.dynamicSettings.IsQueueItOn) {
			redirectToQueueItUrl(requestUrl);
		} else {
			redirectToRegularUrl(requestUrl);
		}
	};

	const button = iconButton({
		dataTestId: TestIdDictionary.SubmitSearchButton,
		isAction: true,
		icon: html`<i class="jsh-circle-chevron-right1 text-base"></i>`,
		isDisabled:
			!props.currentSearchManager.currentSearch.originCity ||
			!props.currentSearchManager.currentSearch.destinationCity ||
			!props.currentSearchManager.currentSearch.departureDate ||
			(!props.currentSearchManager.currentSearch.isOneWay &&
				!props.currentSearchManager.currentSearch.returnDate) ||
			props.currentSearchManager.currentSearch.passengers.adults +
				props.currentSearchManager.currentSearch.passengers.teens ===
				0,
		label: i18next.t("search-box-submit"),
		onClick: handleFormSubmit,
	});

	useEffectWithoutCleanup(() => {
		if (
			userInfo?.RoleCode &&
			!!props.currentSearchManager.currentSearch.useAmericanMiles !== !!userInfo.AmericanAirlines?.IsMember
		) {
			props.currentSearchManager.setUseAmericanMiles(userInfo?.AmericanAirlines?.IsMember);
		}
	}, [userInfo?.RoleCode, props.currentSearchManager.currentSearch.promoCodes.length]);

	const reloadLinkTemplate = () =>
		showReloadLink
			? html`
					<div class="m-0 flex items-center justify-center px-0 pb-0 pt-2 text-center">
						<span
							class="cursor-pointer text-sm font-bold leading-none text-n-blue underline hover:text-brand-primary"
							@click=${handleReloadSearch}
						>
							&#8635;&nbsp;${i18next.t("Rearmar mi última búsqueda")}
						</span>
					</div>
			  `
			: html``;

	const redemptionMilesCheckboxTemplate = () =>
		showRedemptionMiles()
			? html`
					<div class="dg-redemption-checkbox">
						<input
							type="checkbox"
							id=${REDEMPTION_MILES_ID}
							autocomplete="off"
							?checked=${props.currentSearchManager.currentSearch.useAmericanMiles}
							@click=${() =>
								props.currentSearchManager.setUseAmericanMiles(
									!props.currentSearchManager.currentSearch.useAmericanMiles
								)}
						/>
						<label class="ml-2 text-n-blue" for=${REDEMPTION_MILES_ID}
							>${i18next.t("Use AAdvantage® miles")}</label
						>
					</div>
			  `
			: html``;

	const mobileExtraFieldsTemplate = () =>
		props.currentSearchManager.currentSearch.originCity && props.currentSearchManager.currentSearch.destinationCity
			? html` ${dateSelector.htmlTemplate()}${passengerSelector.htmlTemplate()} `
			: "";

	const mobileFlightsTabTemplate = () => html`
		<div class="flex flex-col gap-4">
			<div class="mt-6 grid grid-cols-2 gap-4">
				<div class="col-span-2">${radioToggle.htmlTemplate()}</div>
				<div class="col-span-2">${stationSelector.htmlTemplate()}</div>
				${mobileExtraFieldsTemplate()}
			</div>
			${promoCodeBox.htmlTemplate()} ${redemptionMilesCheckboxTemplate()}
			<div class="flex flex-col gap-4">${reloadLinkTemplate()}${button.htmlTemplate()}</div>
		</div>
	`;

	const desktopFlightsTabTemplate = () => html`
		<div class="mt-4 flex flex-col gap-4">
			<div class="mt-2">${radioToggle.htmlTemplate()}</div>
			<div class="grid w-full grid-cols-[2fr_1fr_1fr_1fr] gap-4">
				${stationSelector.htmlTemplate()} ${dateSelector.htmlTemplate()} ${passengerSelector.htmlTemplate()}
			</div>
			${redemptionMilesCheckboxTemplate()}
			<div
				class=${classNames("flex items-center", {
					"justify-between": showReloadLink,
					"justify-end": !showReloadLink,
				})}
			>
				${reloadLinkTemplate()}
				<div class="flex items-end justify-center gap-4">
					${promoCodeBox.htmlTemplate()} ${button.htmlTemplate()}
				</div>
			</div>
		</div>
	`;

	const htmlTemplate = () =>
		windowSize.size === "XS" || windowSize.size === "SM"
			? html`<div>${mobileFlightsTabTemplate()}</div>`
			: html`<div>${desktopFlightsTabTemplate()}</div> `;

	return { htmlTemplate };
};
